import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StartScreen } from '@/app/start/StartScreen';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { PolicyAcceptanceScreen } from './PolicyAcceptanceScreen';
import type { PolicyAcceptanceStatusEntry } from './policy-acceptance-types';

type AcceptanceGateState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'unblocked' }
  | { status: 'pending'; outstanding: PolicyAcceptanceStatusEntry[] };

/**
 * Resolution 8: wraps `ActiveContextProvider` inside `AuthGate`'s signed-in branch, mounting only
 * once signed in — mirrors `ActiveContextProvider.tsx`'s own fetch/state/branch shape. A `401` is
 * the benign authenticated-but-unprovisioned signal (BK-001), checked BEFORE the generic error
 * branch, exactly as `ActiveContextProvider` already does for the SAME population — it renders
 * `children`, never the blocking screen, because Resolution 7's kill switch structurally cannot
 * rescue this population (`AuthenticationGuard`'s `401` fires before
 * `PolicyAcceptancesController.status()` ever reads `POLICY_ACCEPTANCE_GATE_ENFORCED`). Any other
 * error/non-`ok`/`data === undefined` (5xx, network, malformed — genuine ambiguous reads) fails
 * CLOSED: state `'error'`, never rendering the protected app on an ambiguous read.
 */
export function AcceptanceGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['consent']);
  const [state, setState] = useState<AcceptanceGateState>({ status: 'loading' });

  const load = useCallback(() => {
    setState({ status: 'loading' });

    apiClient
      .GET('/v1/policy-acceptances/status')
      .then(({ data, error, response }) => {
        if (response.status === 401) {
          setState({ status: 'unblocked' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          console.error('acceptance-gate:status-error', { status: response.status });
          setState({ status: 'error' });
          return;
        }
        if (!data.enforced || data.policies.every((policy) => policy.accepted)) {
          setState({ status: 'unblocked' });
          return;
        }
        setState({
          status: 'pending',
          outstanding: data.policies.filter((policy) => !policy.accepted),
        });
      })
      .catch((cause: unknown) => {
        console.error('acceptance-gate:status-error', cause);
        setState({ status: 'error' });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const acceptAll = async (outstanding: PolicyAcceptanceStatusEntry[]) => {
    // Sequential, never parallel (Resolution 8) — at most two calls ever exist.
    for (const policy of outstanding) {
      const { error, response } = await apiClient.POST('/v1/policy-acceptances', {
        body: { policyType: policy.policyType, policyVersion: policy.currentVersion },
      });
      if (error !== undefined || !response.ok) {
        throw new Error(`core-api returned ${response.status} for POST /v1/policy-acceptances`);
      }
    }
    // Every outstanding policy is now confirmed accepted — no extra re-fetch needed.
    setState({ status: 'unblocked' });
  };

  if (state.status === 'loading') {
    // The start screen's second step (#430): the access check.
    return <StartScreen step={1} />;
  }

  if (state.status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="space-y-4 text-center">
          <p className="text-sm text-destructive">{t('consent:gate.error')}</p>
          <Button onClick={load}>{t('consent:gate.retry')}</Button>
        </div>
      </div>
    );
  }

  if (state.status === 'pending') {
    return (
      <PolicyAcceptanceScreen
        outstanding={state.outstanding}
        onAcceptAll={() => acceptAll(state.outstanding)}
      />
    );
  }

  return <>{children}</>;
}
